import { Fragment, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  SendRounded,
  HistoryRounded,
  ArrowBackRounded,
  ArrowForwardRounded,
  DraftsRounded,
  ScheduleRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  WarningAmberRounded,
  ContentCopyRounded,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  Button,
  Divider,
  Alert,
  Snackbar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Paper,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import { audienceLabel } from './campaign-builder/audience-labels';
import {
  ResumenEnvios,
  DetalleCampana,
  ChipRebotes,
  Cifra,
  useRazonSinDatos,
} from './campaign-builder/CampaignResults';
import { metricasCampana } from '../../lib/campaign-metrics';
import { useTenantStore } from '../../stores/tenant.store';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import type { CampaignProps } from '@dosfilos/domain';
import { Step1Content } from './campaign-builder/Step1Content';
import { Step2Audience } from './campaign-builder/Step2Audience';
import { Step3Send } from './campaign-builder/Step3Send';
import DraftsList from './campaign-builder/DraftsList';
import ScheduledList from './campaign-builder/ScheduledList';
import { buildStarterBlocks } from './campaign-builder/utils';
import type { CampaignDraft } from './campaign-builder/types';
import { formatDateTime } from '../../lib/i18n-format';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { pestanaDeLaUrl, PESTANA_POR_DEFECTO, type CampaignTab } from '../../lib/campaign-tabs';
import { callable } from '../../lib/callable';

// ─── Wizard steps ─────────────────────────────────────────────────────────────

const STEPS = ['campaign.steps.content', 'campaign.steps.audience', 'campaign.steps.send'];

// ─── History row type ─────────────────────────────────────────────────────────

type HistoryRow = Pick<
  CampaignProps,
  | 'id'
  | 'subject'
  | 'audienceType'
  | 'segmentTag'
  | 'sentCount'
  | 'failedCount'
  | 'sentAt'
  | 'status'
  // LOS CONTADORES DEL WEBHOOK. Son opcionales a propósito: las campañas
  // anteriores al 27-09-2026 no los tienen, y `undefined` es lo que permite
  // decir «sin datos» en vez de un cero que afirmaría que nadie las recibió.
  | 'deliveredCount'
  | 'openedCount'
  | 'clickedCount'
  | 'bouncedCount'
  | 'complainedCount'
> & {
  failedRecipients?: { email: string; reason: string }[];
  /** Por qué no salió: `module_off` = Captación estaba apagado al llegar su
   *  hora (no sale después; se reprograma a mano). */
  notSentReason?: string;
  htmlContent?: string; // stored to enable reuse
  blocksJson?: string; // stored to enable reuse
};

/** Copia los contadores del proveedor SÓLO SI EXISTEN.
 *
 *  UN `?? 0` ACÁ ROMPERÍA TODO EL TABLERO: convertiría la ausencia de dato de
 *  las campañas viejas en un cero indistinguible de «no la recibió nadie». La
 *  frontera con Firestore es donde se pierde esa diferencia si uno se descuida,
 *  así que se preserva acá y `metricasCampana` decide el resto. */
function contadoresDelProveedor(data: Record<string, unknown>): Partial<CampaignProps> {
  const salida: Record<string, number> = {};
  for (const k of [
    'deliveredCount',
    'openedCount',
    'clickedCount',
    'bouncedCount',
    'complainedCount',
  ] as const) {
    const v = data[k];
    if (typeof v === 'number') salida[k] = v;
  }
  return salida as Partial<CampaignProps>;
}

// ─── Default draft ────────────────────────────────────────────────────────────

function createEmptyDraft(tenantName: string, primaryColor: string): CampaignDraft {
  return {
    subject: '',
    blocks: buildStarterBlocks('blank', tenantName, primaryColor),
    audienceSegment: { type: 'all_crm' },
  };
}

// ─── Step validation ──────────────────────────────────────────────────────────
//
// DEVUELVEN UNA CLAVE, NO UNA FRASE. Son funciones de módulo, así que no pueden
// usar `t` —los hooks sólo viven dentro de un componente— y devolver el texto en
// español lo dejaría fijo para siempre. La pantalla resuelve la palabra, que es
// el mismo patrón que el proyecto usa para los errores del dominio.

function validateStep1(draft: CampaignDraft): string | null {
  if (!draft.subject.trim()) return 'campaign.validation.subjectRequired';
  if (draft.blocks.length === 0) return 'campaign.validation.blocksRequired';
  return null;
}

function validateStep2(draft: CampaignDraft): string | null {
  const seg = draft.audienceSegment;
  if (seg.type === 'tag' && !seg.tag?.trim()) return 'campaign.validation.tagRequired';
  if (seg.type === 'by_person_type' && (!seg.personTypes || seg.personTypes.length === 0))
    return 'campaign.validation.personTypeRequired';
  if (seg.type === 'by_institutional_role' && (!seg.roles || seg.roles.length === 0))
    return 'campaign.validation.roleRequired';
  if (seg.type === 'by_program' && !seg.programId) return 'campaign.validation.programRequired';
  if (seg.type === 'by_course' && !seg.courseId) return 'campaign.validation.courseRequired';
  if (seg.type === 'custom_contacts' && (!seg.contactIds || seg.contactIds.length === 0))
    return 'campaign.validation.contactRequired';
  return null;
}

const saveDraftFn = callable('saveCampaignDraft');

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CrmCampaignsPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId, currentTenantTheme } = useTenantStore();

  // NO VA POR i18n A PROPÓSITO: es el nombre del centro cuando no hay uno
  // configurado, y un nombre propio no se traduce. Queda como dato de respaldo,
  // no como texto de pantalla.
  const tenantName = currentTenantTheme?.name ?? 'Seminario Teológico';
  const primaryColor = currentTenantTheme?.primaryColor ?? '#1e3a5f';
  const logoUrl = currentTenantTheme?.logoUrl;

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  //
  // LA PESTAÑA VIVE EN LA URL, y se DERIVA de ella en vez de copiarse a estado.
  // Guardada en un `useState`, recargar la página devolvía siempre a «Nueva
  // campaña» —se perdía dónde estabas— y no se podía pasarle a nadie el enlace
  // del historial. Y una copia en estado junto a la de la URL es la forma
  // conocida de que las dos se desalineen: acá hay una sola.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = pestanaDeLaUrl(searchParams.get('tab'));
  /**
   * `push` cuando la pestaña la elige la persona, para que «atrás» la devuelva
   * a la anterior — que es lo que espera de unas pestañas con URL.
   *
   * `replace` SÓLO cuando volver atrás llevaría a algo que ya no tiene sentido:
   * después de enviar o programar, «atrás» daría con la pantalla de redacción
   * de una campaña que ya salió.
   *
   * NO BASTA CON QUE EL CAMBIO LO HAGA EL SISTEMA. Retomar un borrador también
   * lo hace, y ahí la lista de borradores sigue perfectamente vigente — usa
   * `push`. La pregunta es si el sitio anterior sigue siendo un lugar válido,
   * no quién movió la pestaña.
   */
  const setTab = (v: CampaignTab, modo: 'push' | 'replace' = 'push') =>
    // SE TOCA SÓLO `tab`, no la cadena de consulta entera. Pasando un objeto
    // nuevo se borraría cualquier otro parámetro que la pantalla llegue a usar
    // —un filtro, un identificador de campaña— y el enlace perdería la mitad de
    // lo que decía. La pestaña de entrada quita el parámetro en vez de
    // escribirlo, para que la URL limpia sea la de siempre.
    setSearchParams(
      (prev) => {
        if (v === PESTANA_POR_DEFECTO) prev.delete('tab');
        else prev.set('tab', v);
        return prev;
      },
      { replace: modo === 'replace' },
    );

  // ── Wizard state ─────────────────────────────────────────────────────────────
  const [activeStep, setActiveStep] = useState(0);
  const [draft, setDraft] = useState<CampaignDraft>(() =>
    createEmptyDraft(tenantName, primaryColor),
  );
  const [stepError, setStepError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // ── Auto-save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  /** EL FALLO DEL GUARDADO TIENE QUE VERSE. Antes el `catch` sólo escribía en
   *  la consola y apagaba el indicador, así que la pantalla quedaba idéntica a
   *  un guardado exitoso: quien cerraba la pestaña perdía el trabajo creyendo
   *  que estaba a salvo. Es la misma forma del 14 de marzo — un fallo que se ve
   *  como éxito. Se mantiene hasta el siguiente guardado bueno. */
  const [saveError, setSaveError] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** EL BORRADOR VIGENTE, PARA EL GUARDADO DIFERIDO.
   *
   *  El guardado espera 1,5 s desde la última tecla, y en ese rato puede haber
   *  más de una edición. Leyendo `draft` de la vuelta de render en que se armó
   *  el temporizador, dos ediciones en el MISMO turno componían mal: la segunda
   *  partía del borrador anterior y la primera se perdía. En una pantalla cuyo
   *  único propósito es no perder lo escrito, eso no puede depender de cuándo
   *  React vuelva a dibujar. */
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  function patchDraft(patch: Partial<CampaignDraft>) {
    // Se compone sobre la REF y no sobre `draft`, así dos ediciones seguidas en
    // el mismo turno se suman en vez de pisarse.
    const siguiente = { ...draftRef.current, ...patch };
    draftRef.current = siguiente;
    setDraft(siguiente);
    setStepError(null);

    // El guardado espera 1,5 s desde la última tecla.
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      guardarBorrador(draftRef.current);
    }, 1500);
  }

  /**
   * LA ÚNICA RUTA DE GUARDADO. Antes estaba escrita dos veces —el guardado
   * automático y el avance de paso— con los mismos seis campos y la misma
   * condición. Ya sabemos cómo termina eso en este repositorio: se corrige una
   * y la otra queda atrás.
   *
   * NO EXIGE ASUNTO. Un borrador es algo a medio hacer; negarse a guardarlo
   * porque le falta una parte es negarse a cumplir su único propósito. Quien
   * escribía el correo entero sin haber pensado el asunto perdía todo al
   * recargar.
   */
  const guardarBorrador = useCallback(
    async (actual: CampaignDraft) => {
      if (!currentTenantId) return;
      setSaving(true);
      try {
        const res = await saveDraftFn({
          tenantId: currentTenantId,
          subject: actual.subject,
          htmlContent: '',
          audienceSegment: actual.audienceSegment,
          blocksJson: JSON.stringify(actual.blocks),
          draftId: actual.draftId,
        });
        const data = res.data as { draftId: string };
        // Se guarda el draftId para que el siguiente guardado actualice el
        // mismo documento en vez de crear otro.
        setDraft((prev) => ({ ...prev, draftId: data.draftId }));
        setSaveError(false);
      } catch (e) {
        console.warn('[AutoSave] Failed to save draft:', e);
        setSaveError(true);
      } finally {
        setSaving(false);
      }
    },
    [currentTenantId],
  );

  async function handleNext() {
    const err =
      activeStep === 0 ? validateStep1(draft) : activeStep === 1 ? validateStep2(draft) : null;
    if (err) {
      // `err` es una CLAVE, no una frase: acá se resuelve la palabra.
      setStepError(t(err));
      return;
    }
    setStepError(null);

    // Al avanzar de paso se guarda, por la MISMA ruta que el guardado
    // automático. Escrita dos veces, la condición y los campos se desalineaban.
    await guardarBorrador(draftRef.current);

    setActiveStep((s) => s + 1);
  }

  function handleBack() {
    setStepError(null);
    setActiveStep((s) => s - 1);
  }

  function handleSent(result?: { scheduled?: boolean }) {
    if (result?.scheduled) {
      setSuccessMsg(t('campaign.history2.scheduledOk'));
      setTimeout(() => {
        setDraft(createEmptyDraft(tenantName, primaryColor));
        setSaveError(false);
        setActiveStep(0);
        setTab('scheduled', 'replace');
      }, 3000);
    } else {
      setSuccessMsg(t('campaign.history2.sentOk'));
      setTimeout(() => {
        setDraft(createEmptyDraft(tenantName, primaryColor));
        setSaveError(false);
        setActiveStep(0);
        setTab('history', 'replace');
        loadHistory();
      }, 3000);
    }
  }

  /** Called from DraftsList when user clicks "Retomar" */
  function handleResumeDraft(resumed: CampaignDraft & { draftId: string }) {
    setDraft(resumed);
    draftRef.current = resumed;
    setActiveStep(0);
    // `push`, no `replace`: apliqué mal mi propia regla. Se usa `replace` cuando
    // volver atrás llevaría a algo que ya no tiene sentido —la pantalla de una
    // campaña enviada—, y la lista de borradores sigue perfectamente vigente:
    // el borrador está ahí y se sigue editando. Con `replace`, «atrás» sacaba
    // de la página en vez de devolver a la lista.
    setTab('compose');
    setStepError(null);
    // EL AVISO DE ERROR NO SE ARRASTRA. Pegado de otro borrador, diría que este
    // no se guardó cuando nadie lo ha intentado todavía — información falsa,
    // que es justo lo que este cambio vino a quitar.
    setSaveError(false);
  }

  // ── History ───────────────────────────────────────────────────────────────────
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expandedFailRow, setExpandedFailRow] = useState<string | null>(null);

  /** Lo que pasó con los correos de cada campaña, calculado una sola vez.
   *
   *  La cuenta vive en `lib/campaign-metrics.ts` porque la comparten la fila,
   *  el detalle desplegado y la franja de arriba: escrita en cada uno se
   *  desalinearía, y la franja diría un porcentaje y la fila otro. */
  const razonSinDatosDe = useRazonSinDatos();
  const metricas = useMemo(
    () => new Map(history.map((r) => [r.id, metricasCampana(r)])),
    [history],
  );

  const loadHistory = useCallback(async () => {
    if (!currentTenantId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(
          collection(db, `tenants/${currentTenantId}/campaigns`),
          // TAMBIÉN LAS FALLIDAS. Filtrar sólo por `sent` las haría desaparecer
          // justo cuando hay que mirarlas: una campaña que no salió es la que
          // más necesita verse en el historial.
          where('status', 'in', ['sent', 'failed']),
          orderBy('updatedAt', 'desc'),
          limit(30),
        ),
      );
      setHistory(
        snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            subject: data['subject'] ?? '',
            status: data['status'] ?? 'sent',
            audienceType: data['audienceType'] ?? 'all_crm',
            segmentTag: data['segmentTag'] ?? undefined,
            sentCount: data['sentCount'] ?? 0,
            failedCount: data['failedCount'] ?? 0,
            ...contadoresDelProveedor(data),
            sentAt: data['sentAt']?.toDate() ?? data['updatedAt']?.toDate() ?? new Date(),
            // EL CAMPO GUARDADO ES `bodyHtml`. Leer `htmlContent` daba siempre
            // `undefined`, así que «Reutilizar» perdía el contenido de toda
            // campaña que no tuviera bloques.
            htmlContent: data['bodyHtml'] ?? undefined,
            blocksJson: data['blocksJson'] ?? undefined,
            ...(typeof data['notSentReason'] === 'string'
              ? { notSentReason: data['notSentReason'] as string }
              : {}),
            ...(Array.isArray(data['failedRecipients']) && data['failedRecipients'].length > 0
              ? {
                  failedRecipients: data['failedRecipients'] as { email: string; reason: string }[],
                }
              : {}),
          };
        }),
      );
    } catch (e: any) {
      console.error('Error loading campaign history', e);
      setHistoryError(t('campaign.history2.loadError'));
    } finally {
      setHistoryLoading(false);
    }
  }, [currentTenantId]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  /** Pre-fill the compose form with an existing sent campaign and switch to compose tab. */
  function handleReuseCampaign(row: HistoryRow) {
    const blocks = (() => {
      if (row.blocksJson) {
        try {
          return JSON.parse(row.blocksJson);
        } catch {
          /* ignore */
        }
      }
      // No blocks stored — create a single HTML block from the raw htmlContent
      if (row.htmlContent) {
        return [{ id: 'reuse-1', type: 'text' as const, content: row.htmlContent }];
      }
      return buildStarterBlocks('blank', tenantName, primaryColor);
    })();

    setDraft({
      subject: row.subject,
      blocks,
      audienceSegment: { type: 'all_crm' }, // reset audience — user picks a new one
      // draftId intentionally omitted — treat as a brand-new draft
    });
    setActiveStep(0);
    setStepError(null);
    // `push` y no `replace`: quien pulsa «Reutilizar» por error espera que
    // «atrás» lo devuelva al historial, que sigue siendo un lugar válido.
    setTab('compose');
    setSuccessMsg(t('campaign.history2.reused', { asunto: row.subject }));
  }

  // Cleanup auto-save timer on unmount
  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    },
    [],
  );

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Tabs */}
      {/* Los `satisfies CampaignTab` de abajo no son adorno: MUI tipa `value`
          como `any`, así que una pestaña mal escrita acá compilaría y su enlace
          caería en «Nueva campaña» sin que nadie entienda por qué. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value={'compose' satisfies CampaignTab}
          label={t('campaign.tabs.newCampaign')}
          icon={<SendRounded fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          value={'drafts' satisfies CampaignTab}
          label={t('campaign.tabs.drafts')}
          icon={<DraftsRounded fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          value={'scheduled' satisfies CampaignTab}
          label={t('campaign.tabs.scheduled')}
          icon={<ScheduleRounded fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          value={'history' satisfies CampaignTab}
          label={t('campaign.tabs.history')}
          icon={<HistoryRounded fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      {/* ── COMPOSE TAB ─────────────────────────────────────────────────────── */}
      {tab === 'compose' && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header row: Stepper + auto-save indicator */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 4 }}
            >
              <Stepper activeStep={activeStep} sx={{ flex: 1 }}>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{t(label)}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {saving && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2 }}>
                  <CircularProgress size={12} />
                  <Typography variant="caption" color="text.secondary">
                    {t('campaign.draft.saving')}
                  </Typography>
                </Stack>
              )}
              {/* EL FALLO GANA AL «GUARDADO». Si el último intento falló, decir
                  «✓ Borrador guardado» porque hubo uno bueno hace un minuto es
                  precisamente la mentira que hace perder el trabajo. */}
              {!saving && saveError && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2 }}>
                  <WarningAmberRounded sx={{ fontSize: 14, color: 'error.main' }} />
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    {t('campaign.draft.saveFailed')}
                  </Typography>
                </Stack>
              )}
              {draft.draftId && !saving && !saveError && (
                <Typography variant="caption" color="success.main" sx={{ ml: 2 }}>
                  {t('campaign.draft.saved')}
                </Typography>
              )}
            </Stack>

            {stepError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStepError(null)}>
                {stepError}
              </Alert>
            )}

            {/* Step content */}
            {activeStep === 0 && (
              <Step1Content
                draft={draft}
                tenantName={tenantName}
                primaryColor={primaryColor}
                tenantId={currentTenantId ?? ''}
                {...(logoUrl ? { logoUrl } : {})}
                onChange={patchDraft}
              />
            )}

            {activeStep === 1 && currentTenantId && (
              <Step2Audience draft={draft} tenantId={currentTenantId} onChange={patchDraft} />
            )}

            {activeStep === 2 && currentTenantId && (
              <Step3Send draft={draft} tenantId={currentTenantId} onSent={handleSent} />
            )}

            {/* Step navigation */}
            <>
              <Divider sx={{ my: 3 }} />
              <Stack direction="row" justifyContent="space-between">
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRounded />}
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{ borderRadius: 2 }}
                >
                  {t('campaign.tabs.back')}
                </Button>
                {activeStep < 2 && (
                  <Button
                    variant="contained"
                    endIcon={
                      saving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <ArrowForwardRounded />
                      )
                    }
                    onClick={handleNext}
                    disabled={saving}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    {activeStep === 1 ? t('campaign.steps.goToSend') : t('campaign.steps.next')}
                  </Button>
                )}
              </Stack>
            </>
          </CardContent>
        </Card>
      )}

      {/* ── DRAFTS TAB ──────────────────────────────────────────────────────── */}
      {tab === 'drafts' && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('campaign.tabs.savedDrafts')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <DraftsList onResume={handleResumeDraft} />
          </CardContent>
        </Card>
      )}

      {/* ── SCHEDULED TAB ───────────────────────────────────────────────────── */}
      {tab === 'scheduled' && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('campaign.tabs.scheduledCampaigns')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ScheduledList />
          </CardContent>
        </Card>
      )}

      {/* ── HISTORY TAB ─────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                {t('campaign.tabs.sentCampaigns')}
              </Typography>
              <Button size="small" onClick={loadHistory} disabled={historyLoading}>
                {historyLoading ? <CircularProgress size={16} /> : t('campaign.tabs.refresh')}
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {/* LA FRANJA VA FUERA DEL TERNARIO y no dentro de la rama de
                la tabla: metida ahí, un fragmento envolvente reindentaba
                450 líneas que no cambiaron y enterraba el cambio real en
                el diff. No se dibuja sola si nada está medido. */}
            {history.length > 0 && <ResumenEnvios rows={history} />}
            {historyLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
                ))}
              </Stack>
            ) : historyError ? (
              <Alert
                severity="error"
                action={
                  <Button size="small" onClick={loadHistory}>
                    {t('campaign.tabs.retry')}
                  </Button>
                }
              >
                {historyError}
              </Alert>
            ) : history.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <HistoryRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">{t('campaign.history2.empty')}</Typography>
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, overflowX: 'auto', overflowY: 'hidden' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>
                        <strong>{t('campaign.results.colSubject')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('campaign.results.colAudience')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{t('campaign.results.sent')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{t('campaign.results.delivered')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{t('campaign.results.opened')}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{t('campaign.results.colFailed')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('campaign.results.colSentAt')}</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row) => (
                      <Fragment key={row.id}>
                        <TableRow hover>
                          <TableCell
                            sx={{
                              maxWidth: 280,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              {row.subject}
                              {row.status === 'failed' && (
                                // No salió ni un correo. Sin esta marca la fila
                                // se lee igual que una enviada con éxito, que
                                // es justo lo que ocurrió el 14 de marzo.
                                <Chip
                                  size="small"
                                  color="error"
                                  label={
                                    row.notSentReason === 'module_off'
                                      ? t('campaign.history.notSentModuleOff')
                                      : row.failedRecipients?.every(
                                            (f) => f.reason === 'email_suppressed',
                                          )
                                        ? t('campaign.history.notSentSuppressed')
                                        : t('campaign.history.notSent')
                                  }
                                  sx={{ fontWeight: 700 }}
                                />
                              )}
                              {/* UN REBOTE ES LO ÚNICO ACCIONABLE de todo esto
                              —esa dirección hay que corregirla— así que se
                              ve sin abrir el detalle. */}
                              <ChipRebotes rebotes={metricas.get(row.id)?.rebotes} />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                row.audienceType === 'tag'
                                  ? `Etiqueta: ${row.segmentTag ?? ''}`
                                  : audienceLabel(row.audienceType)
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={row.sentCount}
                              size="small"
                              color={row.sentCount > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Cifra
                              valor={metricas.get(row.id)?.entregados}
                              sobre={row.sentCount}
                              sinDatos={razonSinDatosDe(row)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Cifra
                              valor={metricas.get(row.id)?.aperturas}
                              sobre={row.sentCount}
                              sinDatos={razonSinDatosDe(row)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {(row.failedCount ?? 0) > 0 ? (
                              <Tooltip
                                title={
                                  row.failedRecipients
                                    ? t('campaign.tabs.failDetail')
                                    : t('campaign.tabs.noFailDetail')
                                }
                                arrow
                              >
                                <Chip
                                  label={row.failedCount}
                                  size="small"
                                  color="error"
                                  icon={<WarningAmberRounded />}
                                  onClick={
                                    row.failedRecipients
                                      ? () =>
                                          setExpandedFailRow((prev) =>
                                            prev === row.id ? null : row.id,
                                          )
                                      : undefined
                                  }
                                  sx={row.failedRecipients ? { cursor: 'pointer' } : {}}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}
                          >
                            {row.sentAt
                              ? formatDateTime(row.sentAt, {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                })
                              : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ pr: 1, whiteSpace: 'nowrap' }}>
                            <Tooltip
                              title={
                                expandedFailRow === row.id
                                  ? t('campaign.results.collapse')
                                  : t('campaign.results.expand')
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                aria-label={
                                  expandedFailRow === row.id
                                    ? t('campaign.results.collapse')
                                    : t('campaign.results.expand')
                                }
                                aria-expanded={expandedFailRow === row.id}
                                onClick={() =>
                                  setExpandedFailRow((prev) => (prev === row.id ? null : row.id))
                                }
                              >
                                {expandedFailRow === row.id ? (
                                  <ExpandLessRounded fontSize="small" />
                                ) : (
                                  <ExpandMoreRounded fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('campaign.results.reuse')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleReuseCampaign(row)}
                                color="primary"
                              >
                                <ContentCopyRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* ── Detalle del envío ──
                        LA FILA EXISTE SIEMPRE, no sólo cuando hubo fallos:
                        antes se desplegaba únicamente para ver a quién no
                        le llegó, y ahora también hay que poder ver qué pasó
                        cuando TODO salió bien. */}
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{
                              p: 0,
                              borderBottom: expandedFailRow === row.id ? undefined : 'none',
                            }}
                          >
                            <Collapse in={expandedFailRow === row.id} unmountOnExit>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: 'action.hover',
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <DetalleCampana row={row} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>

                        {row.failedRecipients && (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              sx={{
                                p: 0,
                                borderBottom: expandedFailRow === row.id ? undefined : 'none',
                              }}
                            >
                              <Collapse in={expandedFailRow === row.id} unmountOnExit>
                                <Box
                                  sx={{
                                    p: 2,
                                    bgcolor: 'error.50',
                                    borderTop: '1px solid',
                                    borderColor: 'error.200',
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      color="error.dark"
                                      fontWeight={600}
                                    >
                                      Correos que fallaron ({row.failedRecipients.length})
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => setExpandedFailRow(null)}
                                    >
                                      <ExpandMoreRounded sx={{ transform: 'rotate(180deg)' }} />
                                    </IconButton>
                                  </Stack>
                                  <Paper
                                    variant="outlined"
                                    sx={{ borderRadius: 1, overflow: 'hidden' }}
                                  >
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                                          <TableCell>
                                            <strong>{t('campaign.tabs.email')}</strong>
                                          </TableCell>
                                          <TableCell>
                                            <strong>{t('campaign.tabs.failReason')}</strong>
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {row.failedRecipients.map((fr, i) => (
                                          <TableRow key={i}>
                                            <TableCell
                                              sx={{ fontFamily: 'monospace', fontSize: 12 }}
                                            >
                                              {fr.email}
                                            </TableCell>
                                            <TableCell sx={{ color: 'error.main', fontSize: 12 }}>
                                              {fr.reason}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Paper>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Success snackbar ─────────────────────────────────────────────────── */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={8000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ borderRadius: 2 }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
